import { memo, ReactNode } from 'react';
import { TimeSyncService } from '@services/TimeSyncService';

import { BoardThemes, ChessBoard } from '@exports/ChessBoard';
import { ChessWinner, SquareName } from '@libs/chess/types';
import { cx } from '@libs/classnames';
import { useCountdownUntill } from '@libs/hooks/useCountdownUntill';
import { formatSeconds } from '@libs/time';
import {
  GameScoreCirce,
  GameScoreCircleResult,
} from '@ui/components/GameScoreCircle/GameScoreCircle';

import styles from './GameCard.module.css';

/*
    ____,-------------------------------,____
    \   |          Компонент            |   /
    /___|-------------------------------|___\
*/

export type Ticking = {
  color: 'white' | 'black';
  timeFromMs: number;
};

export type Game = {
  id: string;
  fen?: string;
  lastMove?: { from: SquareName; to: SquareName };
  ticking?: Ticking;
  winner?: ChessWinner;
  //
  whiteName?: string;
  blackName?: string;
  //
  whiteRating?: number;
  blackRating?: number;
  //
  white2Name?: string;
  black2Name?: string;
  //
  white2Rating?: number;
  black2Rating?: number;
  //
  whiteTimeMs?: number;
  blackTimeMs?: number;
};

export type Props = Game & {
  className?: string;
  noShadow?: boolean;
  theme?: BoardThemes;
  //
  whiteShield?: ReactNode;
  blackShield?: ReactNode;
  //
  white2Shield?: ReactNode;
  black2Shield?: ReactNode;
  //
  onClick?: () => void;
};

export const GameCard = memo(function GameCard({
  className,
  id,
  theme,
  whiteName,
  blackName,
  white2Name,
  black2Name,
  whiteRating,
  blackRating,
  white2Rating,
  black2Rating,
  fen,
  whiteTimeMs,
  blackTimeMs,
  ticking,
  winner,
  lastMove,
  noShadow,
  onClick,
  whiteShield,
  blackShield,
  white2Shield,
  black2Shield,
}: Props) {
  return (
    <article className={cx(styles.card, className)}>
      <div className={styles.players_slot}>
        <Player
          name={blackName}
          rating={blackRating}
          timeMs={black2Name ? undefined : blackTimeMs}
          tickingFromMs={!black2Name && ticking?.color === 'black' ? ticking.timeFromMs : undefined}
          result={black2Name ? undefined : mapResult(winner, 'black')}
          shield={blackShield}
        />
        {black2Name && (
          <Player
            name={black2Name}
            rating={black2Rating}
            timeMs={blackTimeMs}
            tickingFromMs={ticking?.color === 'black' ? ticking.timeFromMs : undefined}
            result={mapResult(winner, 'black')}
            shield={black2Shield}
          />
        )}
      </div>

      <div className={styles.board} onClick={onClick}>
        {
          <ChessBoard
            key={id}
            theme={theme}
            orientation="white"
            noShadow={noShadow}
            notation="none"
            position={fen}
            winner={winner}
            selectedFrom={lastMove?.from}
            selectedTo={lastMove?.to}
          />
        }
      </div>
      <div className={styles.players_slot}>
        <Player
          name={whiteName}
          rating={whiteRating}
          timeMs={whiteTimeMs}
          tickingFromMs={ticking?.color === 'white' ? ticking.timeFromMs : undefined}
          result={mapResult(winner, 'white')}
          shield={whiteShield}
        />
        {white2Name && <Player name={white2Name} rating={white2Rating} shield={white2Shield} />}
      </div>
    </article>
  );
});

/*
    ____,-------------------------------,____
    \   |           Запчасти            |   /
    /___|-------------------------------|___\
*/

function Player({
  name,
  rating,
  shield,
  timeMs,
  tickingFromMs,
  result,
}: {
  name?: string;
  rating?: number;
  shield?: ReactNode;
  timeMs?: number;
  tickingFromMs?: number;
  result?: GameScoreCircleResult;
}) {
  const isTicking = tickingFromMs !== undefined && timeMs !== undefined;
  const { seconds } = useCountdownUntill(
    isTicking ? TimeSyncService.instance.toClientTimestamp(tickingFromMs + timeMs) : 0,
    !isTicking,
  );

  const displayTime = isTicking
    ? seconds
    : timeMs !== undefined
      ? Math.floor(timeMs / 1000)
      : undefined;

  return (
    <div className={styles.player}>
      <div className={styles.nameContainer}>
        <span className={cx(styles.name, { [styles.skeleton]: !name })}>{name || '\u00A0'}</span>
        {rating !== undefined && <span className={styles.rating}>{rating}</span>}
        {shield && <span className={styles.shield}>{shield}</span>}
      </div>

      {result ? (
        <GameScoreCirce className={styles.score} result={result} />
      ) : displayTime !== undefined ? (
        <span translate="no" className={cx(styles.time, { [styles.ticking]: isTicking })}>
          {formatSeconds({ time: displayTime })}
        </span>
      ) : null}
    </div>
  );
}

/*
    ____,-------------------------------,____
    \   |            Утилиты            |   /
    /___|-------------------------------|___\
*/

function mapResult(
  winner: ChessWinner | undefined,
  color: 'white' | 'black',
): GameScoreCircleResult | undefined {
  if (!winner) return undefined;
  if (winner === 'draw') return 'draw';
  return winner === color ? 'win' : 'defeat';
}
